import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import parse, { domToReact } from 'html-react-parser';
import { useSelector } from 'react-redux';
import { FaBars, FaTimes } from 'react-icons/fa';
import { ezMarkdownParser } from '../utils/parserMarkdown/markdownEngine';

const DOCS_BASE_URL = `${process.env.PUBLIC_URL || ''}/docs/user`;

const normalizeDocPath = path_ => {
  const raw = typeof path_ === 'string' ? path_ : '';
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.replace(/^\/+/, '').replace(/\/+$/, '');
};

function DocsUser() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useSelector(state => state.global.isMobile);
  const [navOpen, setNavOpen] = useState(false);
  const [markdown, setMarkdown] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [manifest, setManifest] = useState(null);
  const [manifestError, setManifestError] = useState('');

  const activeDocPath = useMemo(() => {
    const pathname = location?.pathname || '';
    const prefix = '/docs/user';
    const rest = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : '';
    return normalizeDocPath(rest);
  }, [location?.pathname]);

  const manifestItems = useMemo(() => {
    const sections = Array.isArray(manifest?.sections) ? manifest.sections : [];
    const all = [];
    for (const section of sections) {
      const items = Array.isArray(section?.items) ? section.items : [];
      for (const item of items) {
        if (item && item.file) {
          all.push(item);
        }
      }
    }
    return all;
  }, [manifest]);

  const docsPathToFile = useMemo(() => {
    const map = new Map();
    for (const item of manifestItems) {
      map.set(normalizeDocPath(item.path), item.file);
    }
    return map;
  }, [manifestItems]);

  const docsFileToRoute = useMemo(() => {
    const map = new Map();
    for (const item of manifestItems) {
      const route = item.path ? `/docs/user/${normalizeDocPath(item.path)}` : '/docs/user';
      map.set(item.file, route);
    }
    return map;
  }, [manifestItems]);

  const currentDocFile = docsPathToFile.get(activeDocPath) || 'README.md';

  useEffect(() => {
    if (!isMobile) {
      setNavOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    let isCancelled = false;
    const loadManifest = async () => {
      setManifestError('');
      try {
        const response = await fetch(`${DOCS_BASE_URL}/manifest.json`);
        if (!response.ok) {
          throw new Error('Impossible de charger le manifeste de documentation.');
        }
        const data = await response.json();
        if (!isCancelled) {
          setManifest(data);
        }
      } catch (err) {
        if (!isCancelled) {
          setManifest(null);
          setManifestError(err?.message || 'Erreur de chargement du manifeste.');
        }
      }
    };
    loadManifest();
    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!manifest) {
      return;
    }
    // If the path doesn't exist in the manifest, redirect to docs home.
    if (activeDocPath && !docsPathToFile.has(activeDocPath)) {
      navigate('/docs/user', { replace: true });
    }
  }, [activeDocPath, docsPathToFile, manifest, navigate]);

  useEffect(() => {
    let isCancelled = false;
    const loadDoc = async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = await fetch(`${DOCS_BASE_URL}/${currentDocFile}`);
        if (!response.ok) {
          throw new Error(`Impossible de charger ${currentDocFile}`);
        }
        const content = await response.text();
        if (!isCancelled) {
          setMarkdown(content);
        }
      } catch (err) {
        if (!isCancelled) {
          setMarkdown('');
          setError(err?.message || 'Erreur de chargement de la documentation.');
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };
    loadDoc();
    return () => {
      isCancelled = true;
    };
  }, [currentDocFile]);

  const parsedContent = useMemo(() => {
    if (!markdown) {
      return null;
    }

    const html = ezMarkdownParser(markdown);
    return parse(html, {
      replace: domNode => {
        if (domNode?.type !== 'tag') {
          return;
        }
        if (domNode?.name !== 'a') {
          return;
        }

        const hrefRaw = domNode?.attribs?.href;
        if (!hrefRaw || typeof hrefRaw !== 'string') {
          return;
        }

        // Keep anchors and external links as-is.
        if (
          hrefRaw.startsWith('#') ||
          hrefRaw.startsWith('http://') ||
          hrefRaw.startsWith('https://') ||
          hrefRaw.startsWith('mailto:')
        ) {
          return;
        }

        // Convert local markdown links to SPA navigation.
        // Examples in our docs: ./01-demarrage-rapide.md, README.md, 02-navigation-et-recherche.md
        const [pathPart, hashPart] = hrefRaw.split('#');
        let file = (pathPart || '').trim();
        file = file.replace(/^\.\/+/, '');
        file = file.replace(/^\/+/, '');
        if (file.startsWith('docs/user/')) {
          file = file.slice('docs/user/'.length);
        }

        if (!file.endsWith('.md')) {
          return;
        }

        const route = docsFileToRoute.get(file);
        if (!route) {
          return;
        }

        const to = hashPart ? `${route}#${hashPart}` : route;
        const className = domNode.attribs?.class || domNode.attribs?.className;

        return (
          <Link
            to={to}
            className={className}
          >
            {domToReact(domNode.children)}
          </Link>
        );
      },
    });
  }, [markdown, docsFileToRoute]);

  const navContent = (
    <>
      <div className='p-1 border-b border-primary border-opacity-30 flex items-center justify-between gap-1'>
        <div className='font-condensed font-bold uppercase text-sm text-contrast text-opacity-80'>
          {manifest?.title || 'Documentation'}
        </div>
        {isMobile && (
          <button
            type='button'
            className='p-05 rounded bg-main bg-opacity-30 hover:bg-opacity-50 transition text-contrast'
            onClick={() => setNavOpen(false)}
            aria-label='Fermer le menu'
            title='Fermer'
          >
            <FaTimes />
          </button>
        )}
      </div>
      {manifestError && (
        <div className='px-1 pt-05 text-xs text-red text-opacity-90'>
          {manifestError}
        </div>
      )}
      <nav className='p-1 flex flex-col gap-1'>
        {(Array.isArray(manifest?.sections) ? manifest.sections : []).map((section, sectionIndex) => (
          <div key={section?.title || `section-${sectionIndex}`}>
            <div className='text-xs font-condensed uppercase text-contrast text-opacity-60 px-1 py-05'>
              {section?.title}
            </div>
            {(Array.isArray(section?.items) ? section.items : []).map(item => {
              const path = normalizeDocPath(item?.path);
              const to = path ? `/docs/user/${path}` : '/docs/user';
              const isActive = path === activeDocPath;
              return (
                <Link
                  key={item?.file || to}
                  to={to}
                  onClick={() => {
                    if (isMobile) setNavOpen(false);
                  }}
                  className={`block px-1 py-05 rounded transition ${
                    isActive
                      ? 'bg-primary bg-opacity-40 text-contrast'
                      : 'hover:bg-primary hover:bg-opacity-20'
                  }`}
                >
                  {item?.title || to}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
    </>
  );

  return (
    <div className='h-full w-full flex bg-main text-contrast overflow-hidden relative'>
      {!isMobile && (
        <aside className='w-300 min-w-260 max-w-340 border-r border-primary border-opacity-40 overflow-auto'>
          {navContent}
        </aside>
      )}

      {isMobile && navOpen && (
        <>
          <div
            className='fixed inset-0 bg-main bg-opacity-60 z-40'
            onClick={() => setNavOpen(false)}
          />
          <aside className='fixed inset-y-0 left-0 w-screen85 max-w-340 bg-main border-r border-primary border-opacity-40 overflow-auto z-50'>
            {navContent}
          </aside>
        </>
      )}

      <main className='flex-1 overflow-auto p-1 md:p-2'>
        {isMobile && (
          <div className='sticky top-0 z-10 -mx-1 mb-1 px-1 py-05 backdrop-blur bg-main bg-opacity-70 border-b border-primary border-opacity-20 flex items-center gap-05'>
            <button
              type='button'
              className='p-05 rounded bg-primary bg-opacity-40 hover:bg-opacity-60 transition text-contrast'
              onClick={() => setNavOpen(true)}
              aria-label='Ouvrir le menu'
              title='Menu'
            >
              <FaBars />
            </button>
            <div className='font-condensed font-bold uppercase text-sm text-contrast text-opacity-80 truncate'>
              {manifest?.title || 'Documentation'}
            </div>
          </div>
        )}
        {isLoading && <div className='text-contrast text-opacity-70'>Chargement de la documentation...</div>}
        {!isLoading && error && (
          <div className='text-red border border-red border-opacity-40 rounded p-1 bg-red bg-opacity-10'>
            {error}
          </div>
        )}
        {!isLoading && !error && (
          <article className='prose prose-invert max-w-900 mx-auto text-contrast px-1 md:px-0'>
            {parsedContent}
          </article>
        )}
      </main>
    </div>
  );
}

export default DocsUser;
